import { Link } from '@tanstack/react-router';
import { useCapabilities, useMyServicesCapabilityCoverage } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import { StatusBadge } from '../shared/StatusBadge';
import type { WidgetConfig } from './types';
import type { BusinessCapability } from '../../graphql/types';
import '@tylertech/forge/icon';
import './CapabilityStatisticsWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'capability-statistics',
  title: 'Business Capabilities',
  size: 'medium',
  refreshInterval: 30000,
  showRefresh: true,
  showExpand: false,
};

interface CapabilityStats {
  total: number;
  byLifecycleStatus: Record<string, number>;
  byCriticality: Record<string, number>;
}

type CapabilitiesData = ReturnType<typeof useCapabilities>['data'];

function extractCapabilityStats(data: CapabilitiesData): CapabilityStats | null {
  if (!data?.capabilities?.edges) {
    return null;
  }

  const capabilities = data.capabilities.edges.map((edge) => edge.node);

  const byLifecycleStatus: Record<string, number> = {};
  const byCriticality: Record<string, number> = {};

  capabilities.forEach((cap: BusinessCapability) => {
    byLifecycleStatus[cap.lifecycleStatus] = (byLifecycleStatus[cap.lifecycleStatus] || 0) + 1;
    byCriticality[cap.criticality] = (byCriticality[cap.criticality] || 0) + 1;
  });

  return { total: capabilities.length, byLifecycleStatus, byCriticality };
}

export function CapabilityStatisticsWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useCapabilities(undefined, 2000),
    extractCapabilityStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const { data: coverageData } = useMyServicesCapabilityCoverage();
  const servedByMyServices =
    coverageData?.myServicesCapabilityCoverage?.servedCapabilityIds?.length ?? 0;

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.total === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="capability-statistics-widget__empty">
          <IconElement name="account_tree" className="capability-statistics-widget__empty-icon" />
          <p className="forge-typography--body2">No capabilities available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="capability-statistics-widget__content">
        <div className="capability-statistics-widget__total">
          <div className="capability-statistics-widget__total-number">{stats.total}</div>
          <div className="capability-statistics-widget__total-label forge-typography--label2">
            Total Capabilities
          </div>
        </div>

        <div className="capability-statistics-widget__coverage" data-testid="capability-coverage">
          <IconElement
            name="hub"
            className="capability-statistics-widget__coverage-icon"
            aria-hidden="true"
          />
          <span className="capability-statistics-widget__coverage-count forge-typography--label1">
            {servedByMyServices} of {stats.total}
          </span>
          <span className="capability-statistics-widget__coverage-label forge-typography--body2">
            served by My Services
          </span>
        </div>

        <div className="capability-statistics-widget__section">
          <h4 className="capability-statistics-widget__section-title forge-typography--label2">
            By Lifecycle Status
          </h4>
          <div className="capability-statistics-widget__stats-grid">
            {Object.entries(stats.byLifecycleStatus)
              .sort(([, a], [, b]) => b - a)
              .map(([status, count]) => (
                <div key={status} className="capability-statistics-widget__stat-item">
                  <StatusBadge variant="capabilityLifecycle" value={status} />
                  <span className="capability-statistics-widget__stat-count forge-typography--label1">
                    {count}
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="capability-statistics-widget__section">
          <h4 className="capability-statistics-widget__section-title forge-typography--label2">
            By Criticality
          </h4>
          <div className="capability-statistics-widget__stats-grid">
            {Object.entries(stats.byCriticality)
              .sort(([, a], [, b]) => b - a)
              .map(([criticality, count]) => (
                <div key={criticality} className="capability-statistics-widget__stat-item">
                  <StatusBadge variant="criticality" value={criticality} />
                  <span className="capability-statistics-widget__stat-count forge-typography--label1">
                    {count}
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="capability-statistics-widget__footer">
          <Link
            to="/capabilities"
            search={{ page: 1, pageSize: 25 }}
            className="capability-statistics-widget__view-all forge-typography--body2"
          >
            View all →
          </Link>
        </div>
      </div>
    </DashboardWidget>
  );
}
